import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, TemplateRef, ViewEncapsulation, input, model } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TabsModule } from 'primeng/tabs';
import { BadgeEmphasis, BadgeSeverity } from '../fcp-badge';
import { RequireAtLeastOne } from '../helpers/utility-types';

@Component({
    selector: 'fcp-tabs',
    standalone: true,
    imports: [CommonModule, RouterLink, TabsModule],
    styleUrl: './fcp-tabs.component.less',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    },
    template: `
        <p-tabs
            [value]="value()"
            [scrollable]="scrollable()"
            [lazy]="lazy()"
            [selectOnFocus]="selectOnFocus()"
            [showNavigators]="showNavigators()"
            [tabindex]="tabindex()"
            (valueChange)="value.set($event)"
        >
            <p-tablist>
                <ng-template #previcon>
                    <span class="fc-font-icon fc-font-icon-chevron-left"></span>
                </ng-template>

                @for (tab of tabs(); track tab.value) {
                    <p-tab
                        [value]="tab.value"
                        [routerLink]="tab.route ?? null"
                        [disabled]="tab.disabled ?? false"
                        [class]="tabClass(tab)"
                    >
                        @if (tab.titleTemplate) {
                            <ng-container *ngTemplateOutlet="tab.titleTemplate; context: { tab: tab }"></ng-container>
                        } @else {
                            <span class="fcp-tabs__label">
                                @if (tab.icon) {
                                    <span class="fcp-tabs__icon" [class]="tab.icon"></span>
                                }
                                @if (tab.title) {
                                    <span>{{ tab.title }}</span>
                                }
                                @if (tab.badge !== null && tab.badge !== undefined) {
                                    <span [class]="badgeClass(tab)">
                                        <span class="fcp-tabs__badge-text">{{ tab.badge }}</span>
                                    </span>
                                }
                            </span>
                        }
                    </p-tab>
                }

                <ng-template #nexticon>
                    <span class="fc-font-icon fc-font-icon-chevron-right"></span>
                </ng-template>
            </p-tablist>

            @if (!hidePanels()) {
                <p-tabpanels>
                    @for (tab of tabs(); track tab.value) {
                        <p-tabpanel [value]="tab.value">
                            @if (tab.contentTemplate) {
                                <ng-container
                                    *ngTemplateOutlet="tab.contentTemplate; context: { tab: tab }"
                                ></ng-container>
                            } @else {
                                {{ tab.content }}
                            }
                        </p-tabpanel>
                    }
                </p-tabpanels>
            }
        </p-tabs>
    `
})
export class FcpTabsComponent {
    e2e = input<string | null>(null);
    tabs = input<FcpTab[]>([]);
    value = model<string | number | null>(null);
    scrollable = input<boolean>(false);
    lazy = input<boolean>(false);
    selectOnFocus = input<boolean>(false);
    showNavigators = input<boolean>(true);
    hidePanels = input<boolean>(false);
    tabindex = input<number>(0);

    tabClass(tab: FcpTab): string | null {
        const variantClass = tab.variant ? `tab--${tab.variant}` : '';
        const headerClass = tab.headerClass ?? '';
        const classes = `${headerClass} ${variantClass}`.trim();

        return classes.length ? classes : null;
    }

    badgeClass(tab: FcpTab): string {
        const severity = tab.badgeSeverity ?? BadgeSeverity.Promotional;
        const emphasis = tab.badgeEmphasis ?? BadgeEmphasis.Strong;

        return `fcp-tabs__badge fcp-tabs__badge--${this.badgeSeverityClass(severity)} fcp-tabs__badge--${emphasis}`;
    }

    private badgeSeverityClass(severity: BadgeSeverity): string {
        switch (severity) {
            case BadgeSeverity.Primary:
                return 'primary';
            case BadgeSeverity.Secondary:
                return 'secondary';
            default:
                return 'promotional';
        }
    }
}

interface FcpTabBase {
    value: string | number;
    disabled?: boolean;
    headerClass?: string;
    title?: string;
    icon?: string;
    badge?: string | number;
    badgeSeverity?: BadgeSeverity;
    badgeEmphasis?: BadgeEmphasis;
    content?: string;
    route?: string;
    titleTemplate?: TemplateRef<unknown>;
    contentTemplate?: TemplateRef<unknown>;
    variant?: FcpTabVariant;
}
type FcpTabWithTitle = RequireAtLeastOne<FcpTabBase, 'title' | 'titleTemplate'>;
type FcpTabWithContent = RequireAtLeastOne<FcpTabBase, 'content' | 'contentTemplate'>;

export type FcpTab = FcpTabWithTitle & FcpTabWithContent;

export enum FcpTabVariant {
    Label = 'label',
    IconLabel = 'icon-label',
    LabelBadge = 'label-badge',
    IconLabelBadge = 'icon-label-badge',
    IconOnly = 'icon-only'
}
